import { Component } from '@theme/component';

/**
 * Scroll Steps Component
 *
 * Fullscreen section with step transitions controlled by scroll.
 * Desktop: Section locks at top, wheel changes steps, exits at boundaries.
 * Mobile: Native page scroll with sticky images and scroll-driven overlay.
 *
 * @extends Component
 */
class ScrollStepsComponent extends Component {
  requiredRefs = [
    'desktop', 'gaugeDot', 'gaugeTrack', 'stepContents', 'stepMedias', 'scrollTrack',
    'mobileContainer', 'mobileOverlay'
  ];

  // Desktop state
  #currentStep = 0;
  #isAnimating = false;
  #isLocked = false;
  #canExit = false;
  #exitTimeout = 0;
  #scrollAccumulator = 0;
  #scrollThreshold = 50;

  // Shared
  #isDesktop = false;
  #mediaQuery = window.matchMedia('(min-width: 750px)');
  #totalSteps = 0;
  #isInSnapZone = false;
  #snapCooldown = false;

  // Mobile state
  #lastMobileScrollTop = 0;
  /** @type {Array<{start: number, height: number, type: string, step: number}>} */
  #mobilePhases = [];

  connectedCallback() {
    super.connectedCallback();
    this.#totalSteps = parseInt(this.dataset.totalSteps ?? '0', 10) || 0;
    this.#setupMediaQuery();
    this.#setupDesktopListeners();
    this.#setupMobileListeners();
    this.#setupEditorListeners();
    this.#setupPageScrollDetection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaQuery.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleMediaClick);
    window.removeEventListener('wheel', this.#handleWheel, true);
    window.removeEventListener('scroll', this.#handlePageScroll);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#teardownMobileListeners();
    this.removeAttribute('scroll-lock');
    this.#showHeader();
    if (this.#exitTimeout) clearTimeout(this.#exitTimeout);
  }

  #setupMediaQuery() {
    this.#isDesktop = this.#mediaQuery.matches;
    this.#mediaQuery.addEventListener('change', this.#handleMediaQueryChange);
  }

  /** @param {MediaQueryListEvent} event */
  #handleMediaQueryChange = (event) => {
    this.#isDesktop = event.matches;
    this.removeAttribute('scroll-lock');
    this.#isLocked = false;
    this.#canExit = false;
    this.#showHeader();

    if (this.#isDesktop) {
      this.#teardownMobileListeners();
    } else {
      this.#setupMobileListeners();
    }
  };

  // ===========================================
  // PAGE SCROLL DETECTION - Snap section into view
  // ===========================================

  #setupPageScrollDetection() {
    window.addEventListener('scroll', this.#handlePageScroll, { passive: true });
  }

  #handlePageScroll = () => {
    if (!this.#isDesktop || this.#isLocked || this.#snapCooldown) return;

    const rect = this.#getScrollTrackRect();
    if (!rect) return;

    const inSnapZone = rect.top <= 50 && rect.top >= -50 && rect.bottom >= window.innerHeight - 50;

    if (inSnapZone && !this.#isInSnapZone) {
      this.#isInSnapZone = true;

      if (Math.abs(rect.top) <= 50) {
        window.scrollTo({ top: window.scrollY + rect.top, behavior: 'instant' });
        this.setAttribute('scroll-lock', '');
        this.#isLocked = true;
        this.#hideHeader();
      }
    } else if (!inSnapZone) {
      this.#isInSnapZone = false;
    }
  };

  // ===========================================
  // HEADER VISIBILITY
  // ===========================================

  #hideHeader() {
    document.body.classList.add('scroll-snap-active');
    const header = document.querySelector('floating-header');
    const headerInner = header?.querySelector('.floating-header__inner');
    if (headerInner) {
      headerInner.classList.add('floating-header__inner--hidden');
      document.body.classList.add('header-hidden');
    }
  }

  #showHeader() {
    document.body.classList.remove('scroll-snap-active');
    const header = document.querySelector('floating-header');
    const headerInner = header?.querySelector('.floating-header__inner');
    if (headerInner) {
      headerInner.classList.remove('floating-header__inner--hidden');
      document.body.classList.remove('header-hidden');
    }
  }

  // ===========================================
  // EDITOR LOGIC
  // ===========================================

  #setupEditorListeners() {
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** @param {CustomEvent} event */
  #handleBlockSelect = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(target)) return;

    const stepIndex = parseInt(target.dataset.step ?? '-1', 10);
    if (stepIndex < 0 || stepIndex >= this.#totalSteps) return;

    if (this.#isDesktop) {
      const previousStep = this.#currentStep;
      this.#currentStep = stepIndex;
      this.#animateDesktopContent(previousStep, stepIndex);
      this.#animateDesktopMedia(previousStep, stepIndex);
      this.#updateGaugePosition();
    } else {
      const container = this.refs.mobileContainer;
      if (container && !Array.isArray(container)) {
        const rect = container.getBoundingClientRect();
        const sectionTop = window.scrollY + rect.top;
        window.scrollTo({ top: sectionTop + stepIndex * 2 * window.innerHeight, behavior: 'smooth' });
      }
    }
  };

  // ===========================================
  // DESKTOP LOGIC
  // ===========================================

  #setupDesktopListeners() {
    this.addEventListener('click', this.#handleMediaClick);
    window.addEventListener('wheel', this.#handleWheel, { passive: false, capture: true });
  }

  /** @param {MouseEvent} event */
  #handleMediaClick = (event) => {
    if (this.#totalSteps < 2) return;
    if (!this.#isDesktop) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (this.#isAnimating) return;
    const mediaContainer = target.closest('.scroll-steps__media');
    if (!mediaContainer || !this.contains(mediaContainer)) return;
    if (this.#currentStep !== 0) return;

    if (!this.#isLocked) {
      this.#lockDesktop();
    }

    this.#goToDesktopStep(1);
  };

  /**
   * @returns {DOMRect | null}
   */
  #getScrollTrackRect() {
    const scrollTrack = this.refs.scrollTrack;
    if (!scrollTrack || Array.isArray(scrollTrack)) return null;
    return scrollTrack.getBoundingClientRect();
  }

  /**
   * @returns {boolean}
   */
  #shouldLockDesktop() {
    const rect = this.#getScrollTrackRect();
    if (!rect) return false;
    return rect.top <= 20 && rect.top >= -20 && rect.bottom >= window.innerHeight - 20;
  }

  #lockDesktop() {
    const scrollTrack = this.refs.scrollTrack;
    if (!scrollTrack || Array.isArray(scrollTrack)) return;

    const rect = scrollTrack.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + rect.top, behavior: 'instant' });
    this.setAttribute('scroll-lock', '');
    this.#isLocked = true;
    this.#hideHeader();
  }

  /** @param {'up' | 'down'} direction */
  #unlockDesktopAndExit(direction) {
    this.removeAttribute('scroll-lock');
    this.#isLocked = false;
    this.#canExit = false;
    this.#scrollAccumulator = 0;
    if (this.#exitTimeout) {
      clearTimeout(this.#exitTimeout);
      this.#exitTimeout = 0;
    }
    this.#showHeader();
    this.#snapCooldown = true;
    setTimeout(() => { this.#snapCooldown = false; }, 500);
    const scrollAmount = direction === 'down' ? 1 : -1;
    window.scrollBy({ top: scrollAmount, behavior: 'instant' });
  }

  #resetDesktopExitTimer() {
    if (this.#exitTimeout) clearTimeout(this.#exitTimeout);
    this.#canExit = false;
    this.#exitTimeout = window.setTimeout(() => {
      this.#canExit = true;
    }, 50);
  }

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (!this.#isDesktop) return;

    const scrollingDown = event.deltaY > 0;
    const scrollingUp = event.deltaY < 0;
    const atFirstStep = this.#currentStep === 0;
    const atLastStep = this.#currentStep === this.#totalSteps - 1;

    if (!this.#isLocked && this.#shouldLockDesktop()) {
      if (atFirstStep && scrollingUp) return;
      if (atLastStep && scrollingDown) return;

      event.preventDefault();
      event.stopPropagation();
      this.#lockDesktop();
      return;
    }

    if (!this.#isLocked) return;

    const atExitBoundary = (atFirstStep && scrollingUp) || (atLastStep && scrollingDown);

    if (atExitBoundary) {
      if (this.#canExit) {
        this.#unlockDesktopAndExit(scrollingDown ? 'down' : 'up');
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.#resetDesktopExitTimer();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.#canExit = false;
    if (this.#exitTimeout) {
      clearTimeout(this.#exitTimeout);
      this.#exitTimeout = 0;
    }

    if (this.#isAnimating) return;

    this.#scrollAccumulator += event.deltaY;

    if (Math.abs(this.#scrollAccumulator) >= this.#scrollThreshold) {
      if (this.#scrollAccumulator > 0 && !atLastStep) {
        this.#goToDesktopStep(this.#currentStep + 1);
      } else if (this.#scrollAccumulator < 0 && !atFirstStep) {
        this.#goToDesktopStep(this.#currentStep - 1);
      }
      this.#scrollAccumulator = 0;
    }
  };

  /** @param {number} step */
  #goToDesktopStep(step) {
    if (step < 0 || step >= this.#totalSteps || step === this.#currentStep) return;

    this.#isAnimating = true;
    const previousStep = this.#currentStep;
    this.#currentStep = step;

    this.#animateDesktopContent(previousStep, step);
    this.#animateDesktopMedia(previousStep, step);
    this.#updateGaugePosition();

    const duration = parseInt(
      getComputedStyle(this).getPropertyValue('--ss-animation-duration') || '500',
      10
    );

    setTimeout(() => {
      this.#isAnimating = false;
    }, duration + 50);
  }

  /** @param {number} from @param {number} to */
  #animateDesktopContent(from, to) {
    const contents = this.refs.stepContents;
    if (!Array.isArray(contents)) return;
    contents[from]?.classList.remove('is-active');
    contents[to]?.classList.add('is-active');
  }

  /** @param {number} from @param {number} to */
  #animateDesktopMedia(from, to) {
    const medias = this.refs.stepMedias;
    if (!Array.isArray(medias)) return;
    medias[from]?.classList.remove('is-active');
    medias[to]?.classList.add('is-active');
  }

  #updateGaugePosition() {
    const dot = this.refs.gaugeDot;
    if (!dot || Array.isArray(dot)) return;
    const percentage = this.#totalSteps > 1
      ? (this.#currentStep / (this.#totalSteps - 1)) * 100
      : 0;
    /** @type {HTMLElement} */ (dot).style.top = `${percentage}%`;
  }

  // ===========================================
  // MOBILE LOGIC - Scroll-driven overlay + image crossfade
  // ===========================================

  #setupMobileListeners() {
    this.#computeMobilePhases();
    window.addEventListener('scroll', this.#handleMobileScroll, { passive: true });
    window.addEventListener('resize', this.#handleMobileResize);
  }

  #teardownMobileListeners() {
    window.removeEventListener('scroll', this.#handleMobileScroll);
    window.removeEventListener('resize', this.#handleMobileResize);
  }

  #handleMobileResize = () => {
    this.#computeMobilePhases();
  };

  /**
   * Cubic ease-in-out: slows near 0 and 1, fast in the middle.
   * Makes text "hold" slightly when entering/leaving view.
   * @param {number} t - Linear progress 0→1
   * @returns {number} Eased progress 0→1
   */
  #ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  #computeMobilePhases() {
    const container = this.refs.mobileContainer;
    if (!container || Array.isArray(container)) return;

    const children = container.querySelectorAll('.scroll-steps__mobile-spacer, .scroll-steps__mobile-panel');
    let offset = 0;
    let stepIndex = 0;
    this.#mobilePhases = [];

    for (const child of children) {
      const height = /** @type {HTMLElement} */ (child).offsetHeight;
      const isSpacer = child.classList.contains('scroll-steps__mobile-spacer');
      this.#mobilePhases.push({ start: offset, height, type: isSpacer ? 'spacer' : 'panel', step: stepIndex });
      offset += height;
      if (!isSpacer) stepIndex++;
    }
  }

  /**
   * Scroll handler driven by native page scroll.
   * Uses pre-computed phase boundaries to drive overlay opacity and image crossfade.
   */
  #handleMobileScroll = () => {
    if (this.#isDesktop) return;

    const container = this.refs.mobileContainer;
    if (!container || Array.isArray(container)) return;

    const overlay = this.refs.mobileOverlay;
    if (!overlay || Array.isArray(overlay)) return;

    const rect = container.getBoundingClientRect();
    const vh = window.innerHeight;
    const sectionScrollTop = -rect.top;

    if (sectionScrollTop < 0 || sectionScrollTop > /** @type {HTMLElement} */ (container).offsetHeight - vh) return;
    if (this.#mobilePhases.length === 0) return;

    const firstPhase = this.#mobilePhases[0];
    if (!firstPhase) return;

    let phase = firstPhase;
    for (const p of this.#mobilePhases) {
      if (sectionScrollTop < p.start + p.height) {
        phase = p;
        break;
      }
    }

    const rawProgress = Math.min((sectionScrollTop - phase.start) / phase.height, 1);
    const isSpacerPhase = phase.type === 'spacer';
    const phaseProgress = this.#ease(rawProgress);

    const overlayOpacity = isSpacerPhase ? phaseProgress : 1 - phaseProgress;
    /** @type {HTMLElement} */ (overlay).style.opacity = String(overlayOpacity);

    const scrollingForward = sectionScrollTop >= this.#lastMobileScrollTop;
    this.#lastMobileScrollTop = sectionScrollTop;

    const currentStep = Math.min(phase.step, this.#totalSteps - 1);
    const nextStep = Math.min(currentStep + 1, this.#totalSteps - 1);
    const crossfade = isSpacerPhase ? 0 : phaseProgress;
    this.#updateMobileImages(currentStep, nextStep, crossfade, scrollingForward, phaseProgress, isSpacerPhase);
  };

  /**
   * Set mobile image opacities for crossfade.
   * @param {number} current - Current step index
   * @param {number} next - Next step index
   * @param {number} progress - Crossfade progress 0→1
   * @param {boolean} forward - Scroll direction
   * @param {number} phaseProgress - Raw phase progress 0→1
   * @param {boolean} isSpacerPhase - Whether current phase is a spacer (even)
   */
  #updateMobileImages(current, next, progress, forward, phaseProgress, isSpacerPhase) {
    const isCrossfading = progress > 0 && next !== current;

    this.querySelectorAll('.scroll-steps__mobile-image-layer').forEach((el) => {
      const step = parseInt(/** @type {HTMLElement} */ (el).dataset.step ?? '-1', 10);
      let opacity = 0;

      if (step === current) {
        if (isCrossfading && forward) {
          // Forward: hide current instantly (next fades in, overlay covers it)
          opacity = 0;
        } else if (isCrossfading && !forward) {
          // Reverse: fade in under overlay (1 - progress syncs with overlay opacity)
          opacity = 1 - progress;
        } else {
          // Spacer or snapped text: always visible
          opacity = 1;
        }
      }
      if (step === next && next !== current) {
        opacity = progress;
      }

      /** @type {HTMLElement} */ (el).style.opacity = String(opacity);
    });
  }
}

if (!customElements.get('scroll-steps-component')) {
  customElements.define('scroll-steps-component', ScrollStepsComponent);
}
